import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useScrollTrigger,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';

// Logo placeholder - replace with actual logo later
const Logo = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.secondary.main,
  fontWeight: 700,
  fontSize: '1.5rem',
  '& span': {
    color: theme.palette.primary.main,
  },
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
}));

interface NavItem {
  title: string;
  path: string;
  children?: { title: string; path: string }[];
  // Phone menu only; the desktop bar has no room left for another item
  mobileOnly?: boolean;
}

const navItems: NavItem[] = [
  { title: 'Home', path: '/' },
  { title: 'About', path: '/about' },
  {
    title: 'Services',
    path: '/services',
    children: [
      { title: 'Delivery Services', path: '/services/delivery' },
      { title: 'Junk Removal', path: '/services/junk-removal' },
      { title: 'Moving Services', path: '/services/moving' },
      { title: 'Specialty Services', path: '/services/specialty' },
    ],
  },
  { title: 'Service Areas', path: '/areas' },
  { title: 'Pricing', path: '/pricing' },
  { title: 'FAQ', path: '/faq' },
  { title: 'Project Blog', path: '/blog', mobileOnly: true },
  { title: 'Contact', path: '/contact' },
];

const phoneNumbers = [
  { number: '(920) 255-8952', primary: true },
  { number: '(920) 265-4729', primary: false },
];

interface ElevationScrollProps {
  children: React.ReactElement;
}

function ElevationScroll({ children }: ElevationScrollProps) {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
  });
}

const Header: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicesMenuOpen, setServicesMenuOpen] = useState<null | HTMLElement>(null);
  const [expandedMobileSubmenu, setExpandedMobileSubmenu] = useState<string | null>(null);

  const handleServicesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setServicesMenuOpen(event.currentTarget);
  };

  const handleServicesMenuClose = () => {
    setServicesMenuOpen(null);
  };

  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  const toggleMobileSubmenu = (title: string) => {
    setExpandedMobileSubmenu(expandedMobileSubmenu === title ? null : title);
  };


  // Reset menu state on navigation
  useEffect(() => {
    setDrawerOpen(false);
    setServicesMenuOpen(null);
    setExpandedMobileSubmenu(null);
  }, [location.pathname]);

  const renderMobileMenu = () => (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => toggleDrawer(false)}
      PaperProps={{
        sx: {
          width: '280px',
          padding: 2,
        },
      }}
    >
      <Box display="flex" justifyContent="flex-end" p={1}>
        <IconButton onClick={() => toggleDrawer(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <List>
        {navItems.map((item) => (
          <React.Fragment key={item.title}>
            {item.children ? (
              <>
                <ListItem 
                  button 
                  onClick={() => toggleMobileSubmenu(item.title)}
                  sx={{ 
                    borderBottom: '1px solid rgba(0,0,0,0.1)',
                    fontWeight: 600,
                  }}
                >
                  <ListItemText primary={item.title} />
                  {expandedMobileSubmenu === item.title ? 
                    <KeyboardArrowDownIcon sx={{ transform: 'rotate(180deg)' }} /> : 
                    <KeyboardArrowDownIcon />
                  }
                </ListItem>
                {expandedMobileSubmenu === item.title && (
                  <Box pl={2}>
                    {item.children.map((child) => (
                      <ListItem 
                        button 
                        component={RouterLink} 
                        to={child.path} 
                        key={child.title}
                        sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}
                      >
                        <ListItemText primary={child.title} />
                      </ListItem>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <ListItem 
                button 
                component={RouterLink} 
                to={item.path}
                sx={{ 
                  borderBottom: '1px solid rgba(0,0,0,0.1)',
                  fontWeight: location.pathname === item.path ? 700 : 400,
                  color: location.pathname === item.path ? theme.palette.primary.main : 'inherit',
                }}
              >
                <ListItemText primary={item.title} />
              </ListItem>
            )}
          </React.Fragment>
        ))}
        
        <Box mt={2}>
          {phoneNumbers.map((phone, index) => (
            <Button
              key={index}
              variant={phone.primary ? "contained" : "outlined"}
              color={phone.primary ? "primary" : "secondary"}
              startIcon={<PhoneIcon />}
              fullWidth
              href={`tel:${phone.number.replace(/[^0-9]/g, '')}`}
              size="large"
              sx={{ mb: 1, py: 1.2 }}
            >
              {phone.number}
            </Button>
          ))}
        </Box>
        
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            component={RouterLink}
            to="/contact"
            size="large"
            sx={{ py: 1.5, fontWeight: 700 }}
          >
            Get a Quote
          </Button>
        </Box>
      </List>
    </Drawer>
  );

  return (
    <ElevationScroll>
      <StyledAppBar position="sticky">
        <Container>
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            <RouterLink to="/" style={{ textDecoration: 'none' }}>
              <Logo>
                JBR <span>Delivery</span> LLC
              </Logo>
            </RouterLink>

            {!isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {navItems.filter((item) => !item.mobileOnly).map((item) => (
                  <Box key={item.title} sx={{ mx: 1 }}>
                    {item.children ? (
                      <>
                        <Button
                          aria-haspopup="true"
                          onClick={handleServicesMenuOpen}
                          endIcon={<KeyboardArrowDownIcon />}
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            '&:hover': {
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          {item.title}
                        </Button>
                        <Menu
                          anchorEl={servicesMenuOpen}
                          open={Boolean(servicesMenuOpen)}
                          onClose={handleServicesMenuClose}
                          MenuListProps={{
                            'aria-labelledby': 'services-button',
                          }}
                        >
                          {item.children.map((child) => (
                            <MenuItem
                              key={child.title}
                              component={RouterLink}
                              to={child.path}
                              onClick={handleServicesMenuClose}
                              sx={{
                                minWidth: '200px',
                                fontSize: '0.9rem',
                              }}
                            >
                              {child.title}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : (
                      <Link
                        component={RouterLink}
                        to={item.path}
                        underline="none"
                        sx={{
                          color: location.pathname === item.path 
                            ? theme.palette.primary.main 
                            : theme.palette.text.primary,
                          fontWeight: location.pathname === item.path ? 700 : 500,
                          px: 1.5,
                          py: 2,
                          display: 'inline-block',
                          '&:hover': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        {item.title}
                      </Link>
                    )}
                  </Box>
                ))}

                <Box sx={{ display: 'flex', ml: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    component={RouterLink}
                    to="/contact"
                    sx={{ fontWeight: 700 }}
                  >
                    Get a Quote
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<PhoneIcon />}
                    href={`tel:${phoneNumbers[0].number.replace(/[^0-9]/g, '')}`}
                    sx={{ 
                      fontWeight: 700,
                      display: { xs: 'none', sm: 'flex' },
                    }}
                  >
                    {phoneNumbers[0].number}
                  </Button>
                  <IconButton
                    href={`tel:${phoneNumbers[0].number.replace(/[^0-9]/g, '')}`}
                    color="primary"
                    sx={{ 
                      display: { xs: 'flex', sm: 'none' },
                    }}
                  >
                    <PhoneIcon />
                  </IconButton>
                </Box>
                <IconButton 
                  edge="end" 
                  onClick={() => toggleDrawer(true)}
                  sx={{ color: theme.palette.secondary.main }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
        {renderMobileMenu()}
      </StyledAppBar>
    </ElevationScroll>
  );
};

export default Header;
